import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl, { LngLatBounds, Map, Marker, Popup } from 'mapbox-gl';
import { ITourStep } from 'client/models/ITour';
import './TourMap.scss';

interface ITourMapProps {
  steps: ITourStep[];
  height?: string;
}

const TourMap: React.FC<ITourMapProps> = ({ steps, height = '500px' }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [markers, setMarkers] = useState<Marker[]>([]);

  const stepsWithLocation = useMemo(
    () => steps.filter((s) => s.location?.latitude && s.location?.longitude),
    [steps],
  );

  useEffect(() => {
    if (!mapRef.current) return;
    const token = process.env.RAZZLE_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;
    if (!token) return;

    mapboxgl.accessToken = token;

    const initialMap = new mapboxgl.Map({
      container: mapRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [2.3522, 48.8566],
      zoom: 13,
    });

    initialMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

    initialMap.on('load', () => {
      setMap(initialMap);
    });

    return () => {
      initialMap.remove();
    };
  }, []);

  useEffect(() => {
    if (!map) return;

    const applySteps = () => {
      // Nettoyer markers
      markers.forEach((m) => m.remove());

      // Nettoyer la ligne précédente
      if (map.getLayer('tour-path')) map.removeLayer('tour-path');
      if (map.getSource('tour-path')) map.removeSource('tour-path');

      if (stepsWithLocation.length === 0) {
        setMarkers([]);
        return;
      }

      const newMarkers: Marker[] = [];
      const bounds = new LngLatBounds();

      stepsWithLocation.forEach((step, index) => {
        const lng = step.location!.longitude;
        const lat = step.location!.latitude;

        const el = document.createElement('div');
        el.style.width = '28px';
        el.style.height = '28px';
        el.style.borderRadius = '50%';
        el.style.background = '#1a73e8';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.color = 'white';
        el.style.fontWeight = '700';
        el.style.fontSize = '13px';
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        el.textContent = `${index + 1}`;

        const popupHtml = `
          <div style="padding: 10px; max-width: 250px;">
            <h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600;">
              Étape ${step.order}: ${step.title}
            </h4>
            <p style="margin: 0 0 8px 0; font-size: 14px; color: #666;">
              ${step.description}
            </p>
            ${step.location?.address ? `<p style="margin: 0; font-size: 12px; color: #999;"><i class=\"fas fa-map-marker-alt\"></i> ${step.location.address}</p>` : ''}
            ${step.duration ? `<p style="margin: 4px 0 0 0; font-size: 12px; color: #999;"><i class=\"fas fa-clock\"></i> ${step.duration} min</p>` : ''}
          </div>
        `;

        const popup = new Popup({ offset: 25 }).setHTML(popupHtml);
        const marker = new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).setPopup(popup).addTo(map);

        newMarkers.push(marker);
        bounds.extend([lng, lat]);
      });

      setMarkers(newMarkers);

      if (newMarkers.length === 1) {
        map.flyTo({ center: bounds.getCenter(), zoom: 15 });
      } else {
        map.fitBounds(bounds, { padding: 60, duration: 0 });
      }

      if (stepsWithLocation.length > 1) {
        const coordinates = stepsWithLocation.map((s) => [s.location!.longitude, s.location!.latitude]);
        map.addSource('tour-path', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates,
            },
          },
        });
        map.addLayer({
          id: 'tour-path',
          type: 'line',
          source: 'tour-path',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#1a73e8',
            'line-width': 3,
            'line-opacity': 0.8,
          },
        });
      }
    };

    if (map.isStyleLoaded()) {
      applySteps();
    } else {
      map.once('style.load', applySteps);
    }
  }, [map, stepsWithLocation]);

  return (
    <div className="tour-map-container">
      <div
        ref={mapRef}
        className="tour-map"
        style={{ height, width: '100%', borderRadius: '8px' }}
      />
      {stepsWithLocation.length === 0 && (
        <div className="tour-map-empty">
          <i className="fas fa-map-marked-alt fa-3x text-muted mb-3"></i>
          <p className="text-muted">
            Aucune étape avec localisation disponible.
            <br />
            Ajoutez des coordonnées à vos étapes pour les afficher sur la carte.
          </p>
        </div>
      )}
    </div>
  );
};

export default TourMap;
